import '../../platform.css';
import './academy.css';

import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { userSettingsState } from '../../recoil/atoms/userSettingsState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { playersState } from '../../recoil/atoms/playersState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { academyTableFiltersState } from '../../recoil/atoms/academyTableFiltersState';
import { playerTeamDataSelector } from '../../recoil/selectors/playerTeamDataSelector';
import { teamsState } from '../../recoil/atoms/teamsState';
import { useWindowSize } from '../../../common/hooks/WindowSize';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';

import Fuse from 'fuse.js';

import { translate } from '../../../common/language/translations';
import { DropDown } from '../../components/controls/dropDowns/DropDown';
import { updateSeasonStatsToggles } from '../../services/firestore/userSettings';
import { DropDownWithIcons } from '../../components/controls/dropDowns/DropDownWithIcons';
import { IconButton } from '../../components/controls/buttons/IconButton';
import { OwnTeamTable } from '../../components/tables/ownTeamTable/OwnTeamTable';
import { getEuroToCurrency } from '../../utils/economyUtils';
import { InputField } from '../../components/controls/input/InputField';
import { AddPlayerAcademyView } from '../../components/searchPlayers/AddPlayerAcademyView';
import { StringToAnyMap } from '../../types/common';
import { PlayerId } from '../../types/player';
import { playerRoleToSortOrder } from '../../static/constants/playerRoles';
import { academyRoles } from '../../static/constants/playerRoles';
import { isValidPlayerRole } from '../../utils/playerUtils';
import { getAge } from '../../utils/ageUtils';
import { getMonthsLeftOfContract } from '../../utils/contractUtils';
import { getNextYears } from '../../utils/dateUtils';
import { initialAcademyTableFiltersState } from '../../recoil/atoms/academyTableFiltersState';
import { positionOptionsPlatform } from '../../static/constants/positions';


type AcademyFilters = 'birthYear' | 'contractStatus' | 'teamIds' | 'teamPositions' | 'academyRoles';

export const AcademyTableView: React.FC = () => {

  const { currentUser } = useAuthContext();
  const { width } = useWindowSize();

  const userConfig = useRecoilValue(userConfigState);
  const userSettings = useRecoilValue(userSettingsState);
  const tenantSettings = useRecoilValue(tenantSettingsState);

  const [academyTableFilters, setAcademyTableFilters] = useRecoilState(academyTableFiltersState);

  const players = useRecoilValue(playersState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const playerTeamData = useRecoilValue(playerTeamDataSelector);
  const teams = useRecoilValue(teamsState);
  const [tableData, setTableData] = useState<StringToAnyMap[]>([]);

  const [lastTableSortBy, setLastTableSortBy] = useState<StringToAnyMap | undefined>(undefined);
  const [lastTableSortedOrder, setLastTableSortedOrder] = useState<PlayerId[] | undefined>(undefined);

  const [showFilters, setShowFilters] = useState(userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded);
  const [isAddPlayerView, setIsAddPlayerView] = useState(false);
  const [showAddPlayerView, setShowAddPlayerView] = useState(false);

  const [addPlayerViewSearchString, setAddPlayerViewSearchString] = useState<string>('');
  const [isAddPlayerWithoutData, setIsAddPlayerWithoutData] = useState(false);

  const [currentFilterExpanded, setCurrentFilterExpanded] = useState<AcademyFilters | undefined>(undefined);


  const handleToggleShowFilters = async () => {
    if (!userConfig) return;

    const actionIsToggleOff = userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded;
    if (actionIsToggleOff) {
      setAcademyTableFilters(initialAcademyTableFiltersState);
    }

    const newSeasonStatsToggles = { isAcademyTableFilterSectionExpanded: !userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded };
    await updateSeasonStatsToggles(newSeasonStatsToggles, userConfig.email, userConfig.club, currentUser);
  };


  const addPlayerOverviewData = (player: StringToAnyMap, tenantCountryCode: string) => {
    const numberId = Number(player.id);
    if (!isNaN(numberId) && playerOverviews[numberId]) {
      player['image_url'] = playerOverviews[numberId].image_url;
      player['name'] = playerOverviews[numberId].name;

      // values from player document will have presedence over overview data
      if (!player['birth_date']) player['birth_date'] = playerOverviews[numberId].birth_date;
      if (!player['country_code']) player['country_code'] = playerOverviews[numberId].country_code;
      if (!player['contract_expiration']) player['contract_expiration'] = playerOverviews[numberId].contract_expiration;
      if (!player['market_value']) player['market_value'] = getEuroToCurrency(playerOverviews[numberId].market_value, tenantCountryCode);

      // values that we have in our database
      player['birth_date_in_database'] = playerOverviews[numberId].birth_date;
      player['country_code_in_database'] = playerOverviews[numberId].country_code;
      player['contract_expiration_in_database'] = playerOverviews[numberId].contract_expiration;
      player['market_value_in_database'] = getEuroToCurrency(playerOverviews[numberId].market_value, tenantCountryCode);
    }
  };


  const prepareTableData = () => {

    if (!players || !playerOverviews || !playerTeamData || !teams) return;

    const academyPlayers: StringToAnyMap = {};
    Object.keys(players).forEach(playerId => {
      const player = { ...players[playerId] };
      if (player.isAcademyPlayer) {
        addPlayerOverviewData(player, tenantSettings?.countryCode ?? 'NO');
        academyPlayers[playerId] = player;
      }
    });

    // sort data
    let newTableData: StringToAnyMap[] = [];
    if (!lastTableSortedOrder || academyTableFilters.tableSortBy !== lastTableSortBy) {

      newTableData = Object.values(academyPlayers);
      newTableData.sort((a: StringToAnyMap, b: StringToAnyMap) => {
        const tableSortByToUse = academyTableFilters.tableSortBy ?? { id: 'academyRole', desc: true };

        const sortProperty = tableSortByToUse.id;
        let oneValue = tableSortByToUse.desc ? 1 : -1;
        let minusOneValue = tableSortByToUse.desc ? -1 : 1;

        if (tableSortByToUse.id === 'birth_date') {
          oneValue = oneValue * -1;
          minusOneValue = minusOneValue * -1;
        }

        if (!a[sortProperty]) return oneValue;
        if (!b[sortProperty]) return minusOneValue;

        const aValue = tableSortByToUse.id === 'academyRole'
          ? (isValidPlayerRole(a.academyRole) ? playerRoleToSortOrder[a.academyRole] : 0)
          : a[sortProperty];

        const bValue = tableSortByToUse.id === 'academyRole'
          ? (isValidPlayerRole(b.academyRole) ? playerRoleToSortOrder[b.academyRole] : 0)
          : b[sortProperty];

        return aValue > bValue ? minusOneValue : oneValue;
      });
    }
    else {
      newTableData = lastTableSortedOrder.filter((id: PlayerId) => academyPlayers[id]).map((id: PlayerId) => academyPlayers[id]);
      const newPlayersAdded = Object.values(academyPlayers).filter((player: StringToAnyMap) => !lastTableSortedOrder.includes(player.id));
      newTableData = newTableData.concat(newPlayersAdded);
    }

    setLastTableSortedOrder(newTableData.map((player: StringToAnyMap) => player.id));
    setLastTableSortBy(academyTableFilters.tableSortBy);

    if (academyTableFilters.selectedBirthYear.length > 0) {
      newTableData = newTableData.filter(
        player => {
          const birthYear = player.birth_date ? player.birth_date.split('-')[0] : undefined;
          return academyTableFilters.selectedBirthYear.includes(birthYear);
        }
      );
    }

    if (academyTableFilters.selectedContractStatus.length > 0) {
      newTableData = newTableData.filter(
        player => {
          if (!player.contract_expiration) return false;

          const contractExpirationYear = player.contract_expiration.split('-')[0];
          return academyTableFilters.selectedContractStatus.includes(contractExpirationYear);
        }
      );
    }

    if (academyTableFilters.selectedTeamIds.length > 0) {
      newTableData = newTableData.filter(
        player => (playerTeamData[player.id] && Object.keys(playerTeamData[player.id]).some(teamId => academyTableFilters.selectedTeamIds.includes(teamId)))
      );
    }

    if (academyTableFilters.selectedPositions.length > 0) {
      newTableData = newTableData.filter(
        player => (playerTeamData[player.id] && Object.values(playerTeamData[player.id]).some(position => academyTableFilters.selectedPositions.includes(position)))
      );
    }

    if (academyTableFilters.selectedAcademyRoles.length > 0) {
      newTableData = newTableData.filter(
        player => academyTableFilters.selectedAcademyRoles.includes(player.academyRole)
      );
    }

    if (academyTableFilters.nameSearchString) {
      const fuse = new Fuse(newTableData, { keys: ['name'], threshold: 0.2, shouldSort: academyTableFilters.tableSortBy === undefined });
      newTableData = fuse.search(academyTableFilters.nameSearchString).map(result => result.item);
    }

    // compute summary data
    let totalNumberOfPlayers = 0;
    let [totalAge, playersWithAge]: [number, number] = [0, 0];
    let [totalMonthsLeftOfContract, playersWithMonthsLeftOfContract] = [0, 0];

    let totalMarketValue = 0;
    let totalSalary = 0;

    const countryCodeMap: { [key: string]: number } = {};
    const roleMap: { [key: string]: number } = {};

    newTableData.forEach((player: StringToAnyMap) => {
      totalNumberOfPlayers++;

      const age = getAge(player.birth_date);
      if (age) {
        totalAge += age;
        playersWithAge++;
      }

      if (player.contract_expiration) {
        totalMonthsLeftOfContract += getMonthsLeftOfContract(player.contract_expiration) as number;
        playersWithMonthsLeftOfContract++;
      }

      if (player.market_value) totalMarketValue += player.market_value;
      if (player.salary) totalSalary += player.salary;

      if (player.country_code) {
        if (countryCodeMap[player.country_code]) {
          countryCodeMap[player.country_code]++;
        }
        else {
          countryCodeMap[player.country_code] = 1;
        }
      }

      if (player.academyRole) {
        if (roleMap[player.academyRole]) {
          roleMap[player.academyRole]++;
        }
        else {
          roleMap[player.academyRole] = 1;
        }
      }
    });

    const nationalCountryCode = tenantSettings?.countryCode ?? 'NO';
    let numberOfNatives = 0;
    let numberOfForeigners = 0;
    for (const code in countryCodeMap) {
      if (code === nationalCountryCode) {
        numberOfNatives += countryCodeMap[code];
      }
      else {
        numberOfForeigners += countryCodeMap[code];
      }
    }

    newTableData.push({
      'name': 'tableSummary',

      'averageAge': playersWithAge ? (Math.round((totalAge / playersWithAge) * 10) / 10) : undefined,
      'averageMonthsLeftOfContract': playersWithMonthsLeftOfContract ? Math.round(totalMonthsLeftOfContract / playersWithMonthsLeftOfContract) : undefined,

      'market_valueTotal': totalMarketValue ? Math.round(totalMarketValue) : undefined,
      'salaryTotal': totalSalary ? Math.round(totalSalary) : undefined,

      'nationalCountryCode': nationalCountryCode,
      'numberOfNatives': numberOfNatives,
      'numberOfForeigners': numberOfForeigners,

      'roleMap': roleMap,

      'numberOfPlayers': totalNumberOfPlayers,
    });

    setTableData(newTableData);
  };


  useEffect(() => {
    prepareTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    players,
    playerOverviews,
    playerTeamData,
    teams,
    tenantSettings?.countryCode,
    academyTableFilters,
  ]);


  useEffect(() => {
    if (!userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded) {
      setShowFilters(false);
    }
    else {
      const timer = setTimeout(() => {
        setShowFilters(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded]);


  useEffect(() => {
    if (!isAddPlayerView) {
      setShowAddPlayerView(false);
    }
    else {
      const timer = setTimeout(() => {
        setShowAddPlayerView(true);
      }, 100);

      return () => clearTimeout(timer);
    }
    setAddPlayerViewSearchString('');
    setIsAddPlayerWithoutData(false);
  }, [isAddPlayerView]);


  return (
    <div className='full-size-container'>

      {currentFilterExpanded && <div className='filter-section-empty-background' onClick={() => setCurrentFilterExpanded(undefined)} />}

      <IconButton
        onClick={handleToggleShowFilters}
        icon={
          userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded
            ? <CloseIcon style={{ fontSize: 20 }} />
            : <FilterAltIcon style={{ fontSize: 20, marginTop: 1 }} />
        }
        size={24}
        style={{ position: 'absolute', top: 19, left: 22, zIndex: 6 }}
        startWhite={true}
        title={!userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded ? translate('showFilters', userConfig?.language) : undefined}
      />

      {userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded && showFilters && (
        <div className='own-team-table-filter-section'>
          <div className='filter-section-drop-down academy-table-filter-section-name'>
            <InputField
              value={academyTableFilters.nameSearchString}
              placeholder={translate(width < 1200 ? 'name' : 'searchForName', userConfig?.language)}
              onChange={(event) => setAcademyTableFilters({ ...academyTableFilters, nameSearchString: event.target.value })}
              resetValue={() => setAcademyTableFilters({ ...academyTableFilters, nameSearchString: '' })}
            />
          </div>

          <div className='filter-section-drop-down-title academy-table-filter-section-age'>
            {translate('birthYear', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down academy-table-filter-section-age'>
            <DropDown
              id={'academy-table-filter-section-age'}
              dropDownOptions={getNextYears(15, -25)}
              selectedOptions={academyTableFilters.selectedBirthYear}
              setSelectedOptions={(value: string[]) => setAcademyTableFilters({ ...academyTableFilters, selectedBirthYear: value })}
              isDropDownExpanded={currentFilterExpanded === 'birthYear'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'birthYear' : undefined)}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              multiSelect={true}
            />
          </div>

          <div className='filter-section-drop-down-title academy-table-filter-section-contract'>
            {translate('contractExpiration', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down academy-table-filter-section-contract'>
            <DropDown
              id={'academy-table-filter-section-contract'}
              dropDownOptions={getNextYears(8)}
              selectedOptions={academyTableFilters.selectedContractStatus}
              setSelectedOptions={(value: string[]) => setAcademyTableFilters({ ...academyTableFilters, selectedContractStatus: value })}
              isDropDownExpanded={currentFilterExpanded === 'contractStatus'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'contractStatus' : undefined)}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              multiSelect={true}
            />
          </div>

          <div className='filter-section-drop-down-title academy-table-filter-section-team'>
            {translate('team', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down academy-table-filter-section-team'>
            <DropDown
              id={'academy-table-filter-section-team'}
              dropDownOptions={teams ? Object.keys(teams).filter(key => teams[key]?.isAcademyTeam) : []}
              selectedOptions={academyTableFilters.selectedTeamIds}
              setSelectedOptions={(value: string[]) => setAcademyTableFilters({ ...academyTableFilters, selectedTeamIds: value })}
              isDropDownExpanded={currentFilterExpanded === 'teamIds'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'teamIds' : undefined)}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              multiSelect={true}
            />
          </div>

          <div className='filter-section-drop-down-title academy-table-filter-section-position'>
            {translate('teamPosition', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down academy-table-filter-section-position'>
            <DropDown
              id={'academy-table-filter-section-position'}
              dropDownOptions={positionOptionsPlatform}
              selectedOptions={academyTableFilters.selectedPositions}
              setSelectedOptions={(value: string[]) => setAcademyTableFilters({ ...academyTableFilters, selectedPositions: value })}
              isDropDownExpanded={currentFilterExpanded === 'teamPositions'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'teamPositions' : undefined)}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              multiSelect={true}
            />
          </div>

          <div className='filter-section-drop-down-title academy-table-filter-section-role'>
            {translate('academyRole', userConfig?.language)}
          </div>
          <div className='filter-section-drop-down academy-table-filter-section-role'>
            <DropDownWithIcons
              id={'academy-table-filter-section-role'}
              dropDownOptions={academyRoles}
              selectedOptions={academyTableFilters.selectedAcademyRoles}
              setSelectedOptions={(value: string[]) => setAcademyTableFilters({ ...academyTableFilters, selectedAcademyRoles: value })}
              isDropDownExpanded={currentFilterExpanded === 'academyRoles'}
              setIsDropDownExpanded={(value: boolean) => setCurrentFilterExpanded(value ? 'academyRoles' : undefined)}
              multiSelect={true}
              defaultDropDownText={'select'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'84vh'}
              useRoleIcons={true}
            />
          </div>

          <IconButton
            onClick={() => setAcademyTableFilters(initialAcademyTableFiltersState)}
            icon={<ReplayIcon style={{ fontSize: 20 }} />}
            size={24}
            style={{ position: 'absolute', top: 38, right: -40, zIndex: 6 }}
            startWhite={true}
            title={translate('resetFilters', userConfig?.language)}
          />
        </div>
      )}

      {showAddPlayerView && (
        <div
          className='academy-table-add-player-view-container fade-in'
          style={{
            top: userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded ? 126 : 56,
          }}
        >
          <AddPlayerAcademyView
            searchString={addPlayerViewSearchString}
            setSearchString={setAddPlayerViewSearchString}
            isAddPlayerWithoutData={isAddPlayerWithoutData}
            setIsAddPlayerWithoutData={setIsAddPlayerWithoutData}
            isFiltersExpanded={userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded ?? false}
          />
        </div>
      )}

      <div
        className='own-team-table-container academy-table-container'
        style={{
          top: userSettings?.seasonStatsToggles?.isAcademyTableFilterSectionExpanded ? 86 : 16,
          left: isAddPlayerView ? 350 : 0,
        }}
      >
        <OwnTeamTable
          data={tableData}
          isAcademyTable={true}
          tableSortBy={academyTableFilters.tableSortBy}
          setTableSortBy={(value: StringToAnyMap | undefined) => setAcademyTableFilters({ ...academyTableFilters, tableSortBy: value })}
          isAddPlayerView={isAddPlayerView}
          setIsAddPlayerView={setIsAddPlayerView}
        />
      </div>

    </div>
  );
};
